import { hasPermissions, type Permission, type UserDto } from '@gestao/shared'
import {
  BarChart3,
  CalendarClock,
  FileUp,
  Gauge,
  Handshake,
  Kanban,
  Landmark,
  LayoutDashboard,
  ListTodo,
  type LucideIcon,
  Megaphone,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  UsersRound,
  Wallet,
} from 'lucide-react'

import * as t from '../../i18n/pt-BR'

/**
 * Navegação do shell. Só entram áreas com rota; cada item só aparece para quem tem a permissão.
 */
export interface NavItem {
  id: string
  label: string
  icon: LucideIcon
  permission: Permission
  /** Permissões alternativas: o item aparece com `permission` ou com qualquer uma destas. */
  alsoVisibleWith?: readonly Permission[]
  /** Permissões exigidas além de `permission` (todas): o painel executivo pede `cases.read_all`. */
  alsoRequires?: readonly Permission[]
  /**
   * Some quando o usuário tem todas estas: "Meu painel" dá lugar ao "Painel" da gestão (a gestão
   * chega ao próprio painel pelo menu da conta e pela paleta).
   */
  hiddenWith?: readonly Permission[]
  path: string
  /** Termos extras para a paleta de comandos. */
  keywords?: string[]
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

const { groups, items } = t.nav

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'cobranca',
    label: groups.cobranca,
    items: [
      {
        id: 'carteira',
        label: items.carteira,
        icon: Wallet,
        permission: 'cases.read',
        path: '/carteira',
        keywords: ['casos', 'clientes', 'inadimplencia'],
      },
      {
        id: 'fila',
        label: items.fila,
        icon: ListTodo,
        permission: 'cases.read',
        path: '/fila',
        keywords: ['hoje', 'prioridade'],
      },
      {
        // Acordos ativos, propostas e parcelas atrasadas. O selo conta as propostas aguardando
        // aprovação (só para quem aprova). A permissão de leitura de acordos é a mesma da
        // leitura de casos (a cobradora vê só os seus).
        id: 'acordos',
        label: items.acordos,
        icon: Handshake,
        permission: 'cases.read',
        path: '/acordos',
        keywords: ['propostas', 'parcelas', 'aprovação', 'quebrados', 'negociação'],
      },
      {
        id: 'campanhas',
        label: items.campanhas,
        icon: Megaphone,
        permission: 'campaigns.manage',
        path: '/campanhas',
        keywords: ['whatsapp', 'e-mail', 'envio', 'mensagens'],
      },
      {
        id: 'lembretes',
        label: items.lembretes,
        icon: CalendarClock,
        permission: 'reminders.manage',
        path: '/lembretes',
        keywords: ['agenda', 'retorno', 'tarefas', 'adiar'],
      },
    ],
  },
  {
    // Sprint 17: CRM de vendas, separado da cobrança (lead nunca entra na carteira nem na fila).
    id: 'vendas',
    label: groups.vendas,
    items: [
      {
        id: 'vendas',
        label: items.vendas,
        icon: Kanban,
        permission: 'sales.read',
        path: '/vendas',
        keywords: ['crm', 'leads', 'funil', 'kanban', 'prospects', 'novos clientes', 'vendedor'],
      },
    ],
  },
  {
    id: 'gestao',
    label: groups.gestao,
    items: [
      {
        id: 'painel',
        label: items.painel,
        icon: LayoutDashboard,
        permission: 'reports.view',
        alsoRequires: ['cases.read_all'],
        path: '/painel',
        keywords: ['dashboard', 'indicadores', 'kpi', 'metas'],
      },
      {
        id: 'meu-painel',
        label: items.meuPainel,
        icon: Gauge,
        permission: 'cases.read',
        hiddenWith: ['reports.view', 'cases.read_all'],
        path: '/painel/meu',
        keywords: ['meu dia', 'minhas metas', 'minha evolucao', 'dashboard'],
      },
      {
        id: 'relatorios',
        label: items.relatorios,
        icon: BarChart3,
        permission: 'reports.view',
        path: '/relatorios',
        keywords: ['pdf', 'graficos'],
      },
      {
        id: 'financeiro',
        label: items.financeiro,
        icon: Landmark,
        permission: 'finance.read',
        path: '/financeiro',
        keywords: [
          'contas',
          'caixa',
          'dre',
          'lancamentos',
          'contas a pagar',
          'contas a receber',
          'baixa',
        ],
      },
    ],
  },
  {
    id: 'dados',
    label: groups.dados,
    items: [
      {
        id: 'importacoes',
        label: items.importacoes,
        icon: FileUp,
        permission: 'imports.run',
        path: '/importacoes',
        keywords: ['erp', 'carga', 'snapshot', 'quarentena', 'upload'],
      },
      {
        id: 'duplicidades',
        label: items.duplicidades,
        icon: UsersRound,
        permission: 'customers.merge',
        path: '/duplicidades',
        keywords: ['mesclar', 'mesclagem', 'duplicados', 'cadastros repetidos'],
      },
    ],
  },
  {
    id: 'administracao',
    label: groups.administracao,
    items: [
      {
        id: 'usuarios',
        label: items.usuarios,
        icon: Users,
        permission: 'admin.users',
        path: '/configuracoes/usuarios',
        keywords: ['acesso', 'perfis', 'equipe'],
      },
      {
        id: 'auditoria',
        label: items.auditoria,
        icon: ScrollText,
        permission: 'admin.audit',
        path: '/auditoria',
        keywords: ['historico', 'trilha', 'acessos', 'integridade', 'logs'],
      },
      {
        id: 'lgpd',
        label: items.lgpd,
        icon: ShieldCheck,
        permission: 'lgpd.manage',
        // A auditoria lê o registro de tratamento.
        alsoVisibleWith: ['admin.audit'],
        path: '/lgpd',
        keywords: [
          'privacidade',
          'titular',
          'anonimizar',
          'exportar dados',
          'consentimento',
          'mascaramento',
          'retencao',
          'registro de tratamento',
        ],
      },
      {
        id: 'configuracoes',
        label: items.configuracoes,
        icon: Settings,
        permission: 'admin.settings',
        // A coordenação gere templates e cobradoras sem ter as demais configurações.
        alsoVisibleWith: [
          'templates.manage',
          'admin.users',
          'admin.roles',
          'admin.collectors',
          'sales.manage',
          'finance.manage',
        ],
        path: '/configuracoes',
        keywords: [
          'dominios',
          'convenios',
          'planos',
          'erp',
          'parametros',
          'regua',
          'templates',
          'alertas',
          'metas',
          'perfis',
          'permissoes',
          'cobradoras',
          'unidades',
          'faixas',
          'integracoes',
          'smtp',
          'funil de vendas',
          'etapas',
          'motivos de perda',
          'contas bancarias',
          'categorias financeiras',
          'centros de custo',
          'fechamento de mes',
        ],
      },
    ],
  },
]

export function canSeeNavItem(
  user: Pick<UserDto, 'permissions'>,
  item: Pick<NavItem, 'permission' | 'alsoVisibleWith' | 'alsoRequires' | 'hiddenWith'>,
): boolean {
  if (!hasPermissions(user.permissions, ...(item.alsoRequires ?? []))) return false
  if (item.hiddenWith?.length && hasPermissions(user.permissions, ...item.hiddenWith)) return false
  return [item.permission, ...(item.alsoVisibleWith ?? [])].some((permission) =>
    hasPermissions(user.permissions, permission),
  )
}

/** Grupos e itens visíveis para o usuário (grupos vazios somem). */
export function visibleNav(user: Pick<UserDto, 'permissions'>): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeNavItem(user, item)),
  })).filter((group) => group.items.length > 0)
}

/**
 * Tela inicial (destino de "/" e do login, D13): quem tem cobradora vinculada abre na Fila do dia;
 * quem vende e não cobra (perfil vendedor) começa no funil; os demais, na primeira área visível
 * (normalmente a carteira).
 */
export function homePath(
  user: Pick<UserDto, 'permissions'> & { collectorId?: string | null },
): string {
  if (
    hasPermissions(user.permissions, 'sales.read') &&
    !hasPermissions(user.permissions, 'cases.read')
  ) {
    return '/vendas'
  }
  const groups = visibleNav(user)
  const paths = groups.flatMap((group) => group.items.map((item) => item.path))
  if (user.collectorId && paths.includes('/fila')) return '/fila'
  return paths[0] ?? '/perfil'
}
